
import { useState, useEffect } from "react";
import { Layers } from "lucide-react";
import { useTranslation } from 'react-i18next';
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

interface ServiceCategory {
  id: number;
  name: string;
  display_name_en: string;
  display_name_zh?: string;
  description?: string;
  sort_order?: number;
}

interface ServiceCategoryFilterProps {
  selectedCategories: string[];
  onCategoryChange: (categoryName: string) => void;
}

const ServiceCategoryFilter = ({ selectedCategories, onCategoryChange }: ServiceCategoryFilterProps) => {
  const { t, i18n } = useTranslation('practitioners');
  const [serviceCategories, setServiceCategories] = useState<ServiceCategory[]>([]);
  const [isLoadingCategories, setIsLoadingCategories] = useState(false);
  const { toast } = useToast();

  // Fetch service categories from Supabase
  useEffect(() => {
    const fetchServiceCategories = async () => {
      setIsLoadingCategories(true);
      try {
        console.log("Fetching service categories...");
        const { data, error } = await supabase
          .from('service_categories')
          .select('*')
          .order('sort_order', { ascending: true })
          .order('id', { ascending: true });

        if (error) throw error;
        
        console.log("Fetched service categories:", data);
        setServiceCategories(data || []);
      } catch (error) {
        console.error("Error fetching service categories:", error);
        toast({
          variant: 'destructive',
          title: "Failed to load service categories",
          description: "Please try refreshing the page.",
        });
      } finally {
        setIsLoadingCategories(false);
      }
    };

    fetchServiceCategories();
  }, [toast]);

  // Debug selected categories
  useEffect(() => {
    console.log("Currently selected categories:", selectedCategories);
  }, [selectedCategories]);

  // Handle category selection - single selection only
  const handleCategorySelect = (categoryName: string) => {
    console.log("Selecting category:", categoryName);
    onCategoryChange(categoryName);
  };

  // Get display name based on current language
  const getDisplayName = (category: ServiceCategory) => {
    if (i18n.language === 'zh' && category.display_name_zh) {
      return category.display_name_zh;
    }
    return category.display_name_en;
  };

  return (
    <div>
      <h3 className="text-sm font-semibold mb-2 flex items-center">
        <Layers className="mr-2 h-4 w-4 text-amber-600" /> {t('serviceStages')}
      </h3>
      
      <Select 
        value={selectedCategories[0] || "all_types"} 
        onValueChange={handleCategorySelect}
        disabled={isLoadingCategories}
      >
        <SelectTrigger className="border-amber-200">
          <SelectValue placeholder={t('selectStage')} />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="all_types">{t('allStages')}</SelectItem>
          {serviceCategories.map((category) => (
            <SelectItem key={category.id} value={category.name}>
              {getDisplayName(category)}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
};

export default ServiceCategoryFilter;
